import { forwardRef, useCallback, useMemo } from "react";
import { useQueryParams } from "../../dataAccess/QueryParams";
import {
  Collection,
  CollectionHeader,
  useCollections,
} from "../../dataAccess/api/collections";
import Filter, {
  ExpandedByDefaultOption,
  FilterCommonProps,
  FilterCommonSettings,
  Option,
} from "../ui/Filter";
import { GridSettings } from "./Grid";
import { PublicComponentProps } from "./PublicComponentProps";

interface Settings extends FilterCommonSettings {
  hideHeadersWhenVertical?: boolean;
  isOpenOnMobile?: ExpandedByDefaultOption;
  isOpenOnDesktop?: ExpandedByDefaultOption;
}

// TODO: remove "any" type in the next lines
interface Props
  extends Pick<FilterCommonProps, "mobile">,
    PublicComponentProps<Settings, any, any, GridSettings> {
  collections?: CollectionHeader[];
}

const loadingCollectionHeader: CollectionHeader[] = [
  { id: -1, name: "", collections: [] },
];

const CollectionsFilter = forwardRef(
  ({ collections, settings, mobile }: Props, ref) => {
    const { collection_ids } = useQueryParams();
    const { loadingCollectionList } = useCollections();
    const collectionToOption = useCallback(
      ({
        id: value,
        name: label,
        numberOfItems,
        ...collection
      }: Collection): Option => ({
        value: String(value),
        label,
        detailsLabel: settings.showNumberOfResults
          ? String(numberOfItems || "")
          : "",
        subOptions: collection.subCollections?.map((subCollection) =>
          collectionToOption(subCollection),
        ),
      }),
      [settings],
    );
    const expandedSettings = useMemo(
      () => (mobile ? settings.isOpenOnMobile : settings.isOpenOnDesktop),
      [mobile, settings],
    );

    const queryParamExist = useMemo(() => {
      if (collection_ids && collection_ids?.length > 0) {
        return true;
      }
    }, [collection_ids]);

    const defaultExpanded = useMemo(
      () =>
        expandedSettings === "open-parent" ||
        expandedSettings === "open-parent-and-children",
      [expandedSettings],
    );

    const childrenDefaultExpanded = useMemo(
      () => expandedSettings === "open-parent-and-children",
      [expandedSettings],
    );

    return collections?.length || loadingCollectionList ? (
      <Filter
        ref={ref}
        alignment={settings.alignment}
        options={(collections || loadingCollectionHeader).map(
          ({ name, collections }) => ({
            title: settings.hideHeadersWhenVertical ? undefined : name,
            values: collections?.map(collectionToOption) || [],
          }),
        )}
        title={settings.title || "Collections"}
        queryParam="collection_ids"
        variant={
          settings.position !== "vertical" ? "horizontal" : settings.position
        }
        mobile={mobile}
        defaultExpanded={queryParamExist || defaultExpanded}
        childrenDefaultExpanded={queryParamExist || childrenDefaultExpanded}
        loading={loadingCollectionList}
        horizontalVariant={settings.horizontalVariant}
      />
    ) : null;
  },
);

export default CollectionsFilter;
